import type { FlexProps, SystemStyleObject } from "@chakra-ui/react";
import { Button, Flex } from "@chakra-ui/react";
import { debounce } from "lodash-es";
import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useIsomorphicLayoutEffect } from "../../../hooks/useIsomorphicLayoutEffect";
import { Box } from "../../layout/box/Box";
import { Text } from "../text/Text";

export interface ReadMoreProps extends FlexProps {
    ariaMore?: string;
    ariaLess?: string;
    showMoreLabel?: string;
    showLessLabel?: string;
    collapsedHeight: string[];
    text?: string;
    textStyle?: object;
    noOfLines?: number;
    showButtonStyle?: SystemStyleObject;
    activateReadMore?: boolean;
}

export const DEFAULT_SHOW_MORE_LABEL = "_Read more";
export const DEFAULT_SHOW_LESS_LABEL = "_Read less";
const DEFAULT_COLLAPSED_HEIGHT = ["2rem"];
const DEFAULT_NUMBER_OF_LINES = 2;

const hasContentOverflowing = (el: HTMLDivElement) => {
    const { clientHeight, scrollHeight } = el;
    return scrollHeight > clientHeight;
};

export const READ_MORE_TEST_ID = "readmore-content";

export const ReadMore: FC<ReadMoreProps> = ({
    children,
    showMoreLabel = DEFAULT_SHOW_MORE_LABEL,
    showLessLabel = DEFAULT_SHOW_LESS_LABEL,
    collapsedHeight = DEFAULT_COLLAPSED_HEIGHT,
    text,
    textStyle,
    noOfLines = DEFAULT_NUMBER_OF_LINES,
    showButtonStyle,
    ariaLess = "",
    ariaMore = "",
    activateReadMore = true,
    ...rest
}): JSX.Element => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [showButton, setShowButton] = useState<boolean>(false);
    const [openHeight, setOpenHeight] = useState<string | "auto">("auto");

    const ref = useRef<HTMLDivElement>(null);

    const handleToggle = () => setIsExpanded((prevState) => !prevState);

    const checkButtonAvailability = useCallback(() => {
        if (ref.current) {
            const isOverflowing = hasContentOverflowing(ref.current);
            setOpenHeight(ref.current.scrollHeight + "px");

            if (isOverflowing !== showButton) {
                setShowButton(isOverflowing);
            }

            if (isExpanded && !isOverflowing) {
                setIsExpanded(false);
            }
        }
    }, [isExpanded, showButton]);

    useIsomorphicLayoutEffect(() => {
        const debouncedCheck = debounce(checkButtonAvailability, 300);

        window.addEventListener("resize", debouncedCheck);

        return () => {
            window.removeEventListener("resize", debouncedCheck);
        };
    }, [checkButtonAvailability]);

    useEffect(() => {
        checkButtonAvailability();
    }, [text]);

    return (
        <Flex flexDirection="column" alignItems="flex-start" {...rest}>
            <Box
                data-testid={READ_MORE_TEST_ID}
                ref={ref}
                overflow="hidden"
                maxHeight={
                    !activateReadMore ? "none" : isExpanded ? `${openHeight}` : collapsedHeight
                }
                transitionDuration={"0.3s"}
                transitionProperty={"max-height, height"}
            >
                <Text
                    sx={textStyle}
                    noOfLines={
                        showButton && !isExpanded && activateReadMore ? noOfLines : undefined
                    }
                >
                    {text}
                </Text>
            </Box>

            {showButton && activateReadMore && (
                <Button
                    aria-label={isExpanded ? ariaLess || showLessLabel : ariaMore || showMoreLabel}
                    background="none"
                    _hover={{ background: "none" }}
                    display="flex"
                    onClick={handleToggle}
                    h="100%"
                    variant="link"
                    sx={showButtonStyle}
                >
                    <Text color="black">{isExpanded ? showLessLabel : showMoreLabel}</Text>
                </Button>
            )}
        </Flex>
    );
};
